import { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Loader2, Upload, Film, Tv, Star, Crown, Search, X,
  Users, DollarSign, KeyRound, Mail, ShoppingBag, Megaphone, Inbox,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const EMPTY = {
  title: "", type: "movie", description: "", genres: "", runtime: 0, release_year: 2025,
  cast: "", director: "", rating: 0, maturity: "All", poster_url: "", backdrop_url: "",
  trailer_url: "", video_url: "", featured: false, trending: false, new_release: false,
  qualities: "720p, 1080p, 4K", audio_tracks: "Burmese, English", subtitles: "Burmese, English, Off",
  seo_title: "", seo_description: "", seo_keywords: "",
  rent_price: 3, buy_price: 15, rent_days: 3,
};

const toArr = (s) => (Array.isArray(s) ? s : String(s || "").split(",").map((x) => x.trim()).filter(Boolean));
const toStr = (a) => (Array.isArray(a) ? a.join(", ") : a || "");
const EMPTY_AD = { name: "", placement: "home_banner", image_url: "", video_url: "", link_url: "", content_id: "", active: true };

function UploadField({ label, field, value, accept, onUploaded }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(field, data.url);
      toast.success(`${label} uploaded`);
    } catch {
      toast.error(`${label} upload failed`);
    } finally { setBusy(false); }
  };
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-white/40">{label}</label>
      <div className="flex gap-2 mt-1">
        <input value={value || ""} onChange={(e) => onUploaded(field, e.target.value)} placeholder="Paste URL or upload" className="flex-1 bg-ink border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-brand" />
        <input ref={ref} type="file" accept={accept} onChange={handle} className="hidden" />
        <button type="button" data-testid={`upload-${field}`} onClick={() => ref.current?.click()} disabled={busy} className="shrink-0 bg-white/10 hover:bg-white/20 rounded-md px-3 flex items-center gap-1 text-sm transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, checked } = useAuth();
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("content");
  const [subs, setSubs] = useState(null);
  const [ads, setAds] = useState(null);
  const [support, setSupport] = useState(null);
  const [adOpen, setAdOpen] = useState(false);
  const [adForm, setAdForm] = useState(EMPTY_AD);
  const [adEditId, setAdEditId] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [resetting, setResetting] = useState(false);

  const load = () => api.get("/admin/content").then((r) => setItems(r.data)).catch(() => setItems([]));
  const loadSubs = () => api.get("/admin/subscribers").then((r) => setSubs(r.data)).catch(() => setSubs({ subscribers: [], total_income: 0, platform_commission: 0, purchases: 0, total_users: 0 }));
  const loadAds = () => api.get("/admin/ads").then((r) => setAds(r.data)).catch(() => setAds([]));
  const loadSupport = () => api.get("/admin/support").then((r) => setSupport(r.data)).catch(() => setSupport([]));
  useEffect(() => { if (user?.role === "admin") load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (user?.role === "admin" && tab === "subscribers" && !subs) loadSubs(); }, [tab, user]); // eslint-disable-line
  useEffect(() => { if (user?.role === "admin" && tab === "ads" && !ads) loadAds(); }, [tab, user]); // eslint-disable-line
  useEffect(() => { if (user?.role === "admin" && tab === "support" && !support) loadSupport(); }, [tab, user]); // eslint-disable-line

  if (checked && (!user || user.role !== "admin")) return <Navigate to="/" replace />;
  if (!items) return <div className="min-h-screen flex items-center justify-center bg-ink"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>;

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (item) => {
    setForm({
      ...EMPTY, ...item,
      genres: toStr(item.genres), cast: toStr(item.cast), qualities: toStr(item.qualities),
      audio_tracks: toStr(item.audio_tracks), subtitles: toStr(item.subtitles),
    });
    setEditingId(item.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    const payload = {
      ...form,
      runtime: Number(form.runtime) || 0,
      release_year: Number(form.release_year) || 2025,
      rating: Number(form.rating) || 0,
      rent_price: Number(form.rent_price) || 0,
      buy_price: Number(form.buy_price) || 0,
      rent_days: Number(form.rent_days) || 3,
      genres: toArr(form.genres), cast: toArr(form.cast), qualities: toArr(form.qualities),
      audio_tracks: toArr(form.audio_tracks), subtitles: toArr(form.subtitles),
    };
    try {
      if (editingId) await api.put(`/admin/content/${editingId}`, payload);
      else await api.post("/admin/content", payload);
      toast.success(editingId ? "Title updated" : "Title created");
      setOpen(false);
      await load();
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/content/${item.id}`);
      setItems((cur) => cur.filter((c) => c.id !== item.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const submitReset = async () => {
    if (newPw.length < 6) return toast.error("Password must be at least 6 characters");
    setResetting(true);
    try {
      await api.post(`/admin/users/${resetUser.id}/reset-password`, { new_password: newPw });
      toast.success(`Password reset for ${resetUser.email}`);
      setResetUser(null);
      setNewPw("");
    } catch { toast.error("Reset failed"); }
    finally { setResetting(false); }
  };

  const sendResetEmail = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/send-reset`);
      toast.success(data.message);
    } catch { toast.error("Failed to send reset link"); }
  };

  const setAd = (f, v) => setAdForm((p) => ({ ...p, [f]: v }));
  const openNewAd = () => { setAdForm(EMPTY_AD); setAdEditId(null); setAdOpen(true); };
  const openEditAd = (a) => { setAdForm({ ...EMPTY_AD, ...a }); setAdEditId(a.id); setAdOpen(true); };
  const saveAd = async () => {
    if (!adForm.name.trim()) return toast.error("Ad name required");
    try {
      if (adEditId) await api.put(`/admin/ads/${adEditId}`, adForm);
      else await api.post("/admin/ads", adForm);
      toast.success("Ad saved"); setAdOpen(false); loadAds();
    } catch { toast.error("Save failed"); }
  };
  const removeAd = async (a) => {
    if (!window.confirm(`Delete ad "${a.name}"?`)) return;
    try { await api.delete(`/admin/ads/${a.id}`); setAds((c) => c.filter((x) => x.id !== a.id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const filtered = items.filter((i) => i.title.toLowerCase().includes(search.toLowerCase()));
  const stats = {
    total: items.length,
    movies: items.filter((i) => i.type === "movie").length,
    series: items.filter((i) => i.type === "series").length,
    featured: items.filter((i) => i.featured).length,
  };

  const StatCard = ({ icon, label, value }) => (
    <div className="bg-surface border border-white/10 rounded-xl p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-lg bg-gold/15 text-gold flex items-center justify-center">{icon}</div>
      <div>
        <p className="font-display text-2xl font-black">{value}</p>
        <p className="text-white/50 text-xs uppercase tracking-widest">{label}</p>
      </div>
    </div>
  );

  return (
    <div className="bg-ink min-h-screen pt-24 px-4 sm:px-12 pb-20">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black flex items-center gap-3"><Crown className="w-8 h-8 text-gold" /> Admin Dashboard</h1>
          <p className="text-white/50">Manage your content library, artwork, video and SEO.</p>
        </div>
        <Button data-testid="admin-add-btn" onClick={openNew} className={`bg-brand hover:bg-brand/90 text-white ${tab === "content" ? "" : "hidden"}`}><Plus className="w-4 h-4 mr-1" /> Add Title</Button>
      </div>

      <div className="flex gap-1 mb-6 border-b border-white/10">
        <button data-testid="tab-content" onClick={() => setTab("content")} className={`px-4 py-2 font-semibold text-sm -mb-px border-b-2 transition-colors ${tab === "content" ? "border-brand text-white" : "border-transparent text-white/50 hover:text-white"}`}>Content Library</button>
        <button data-testid="tab-subscribers" onClick={() => setTab("subscribers")} className={`px-4 py-2 font-semibold text-sm -mb-px border-b-2 transition-colors ${tab === "subscribers" ? "border-brand text-white" : "border-transparent text-white/50 hover:text-white"}`}>Subscribers & Income</button>
        <button data-testid="tab-ads" onClick={() => setTab("ads")} className={`px-4 py-2 font-semibold text-sm -mb-px border-b-2 transition-colors ${tab === "ads" ? "border-brand text-white" : "border-transparent text-white/50 hover:text-white"}`}>Ads</button>
        <button data-testid="tab-support" onClick={() => setTab("support")} className={`px-4 py-2 font-semibold text-sm -mb-px border-b-2 transition-colors ${tab === "support" ? "border-brand text-white" : "border-transparent text-white/50 hover:text-white"}`}>Support Inbox</button>
      </div>

      {tab === "content" && (
      <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={<Film className="w-5 h-5" />} label="Total Titles" value={stats.total} />
        <StatCard icon={<Film className="w-5 h-5" />} label="Movies" value={stats.movies} />
        <StatCard icon={<Tv className="w-5 h-5" />} label="Series" value={stats.series} />
        <StatCard icon={<Star className="w-5 h-5" />} label="Featured" value={stats.featured} />
      </div>

      <div className="flex items-center bg-surface border border-white/10 rounded-lg px-3 py-2 mb-4 max-w-md">
        <Search className="w-4 h-4 text-white/40" />
        <input data-testid="admin-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search titles..." className="bg-transparent outline-none px-2 flex-1 text-sm" />
      </div>

      <div className="bg-surface border border-white/10 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-widest text-white/40 border-b border-white/5">
          <div className="col-span-6 sm:col-span-5">Title</div>
          <div className="col-span-2 hidden sm:block">Type</div>
          <div className="col-span-2">Year</div>
          <div className="col-span-2 hidden sm:block">Rating</div>
          <div className="col-span-4 sm:col-span-1 text-right">Actions</div>
        </div>
        {filtered.map((item) => (
          <div key={item.id} data-testid={`admin-row-${item.id}`} className="grid grid-cols-12 items-center px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors">
            <div className="col-span-6 sm:col-span-5 flex items-center gap-3 min-w-0">
              <img src={item.poster_url} alt="" className="w-10 h-14 object-cover rounded shrink-0" />
              <span className="font-medium truncate">{item.title}</span>
            </div>
            <div className="col-span-2 hidden sm:block capitalize text-white/70">{item.type}</div>
            <div className="col-span-2 text-white/70">{item.release_year}</div>
            <div className="col-span-2 hidden sm:flex items-center gap-1 text-gold"><Star className="w-3.5 h-3.5 fill-gold" /> {item.rating}</div>
            <div className="col-span-4 sm:col-span-1 flex justify-end gap-1">
              <button data-testid={`edit-${item.id}`} onClick={() => openEdit(item)} className="w-8 h-8 rounded-md hover:bg-white/10 flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`delete-${item.id}`} onClick={() => remove(item)} className="w-8 h-8 rounded-md hover:bg-brand/20 text-brand flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-white/40 text-center py-10">No titles.</p>}
      </div>
      </>
      )}

      {tab === "subscribers" && (
        <SubscribersView subs={subs} StatCard={StatCard} onReset={(u) => { setResetUser(u); setNewPw(""); }} onSendReset={sendResetEmail} />
      )}

      {tab === "ads" && (
        <AdsView ads={ads} onNew={openNewAd} onEdit={openEditAd} onRemove={removeAd} />
      )}

      {tab === "support" && (<SupportView support={support} />)}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editingId ? "Edit Title" : "Add New Title"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Title"><input data-testid="form-title" value={form.title} onChange={(e) => set("title", e.target.value)} className={inp} /></Field>
              <Field label="Type">
                <Select value={form.type} onValueChange={(v) => set("type", v)}>
                  <SelectTrigger className="bg-ink border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 text-white">
                    <SelectItem value="movie">Movie</SelectItem>
                    <SelectItem value="series">Series</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Description"><textarea data-testid="form-description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className={inp} /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Genres (comma separated)"><input value={form.genres} onChange={(e) => set("genres", e.target.value)} className={inp} placeholder="Action, Drama" /></Field>
              <Field label="Cast (comma separated)"><input value={form.cast} onChange={(e) => set("cast", e.target.value)} className={inp} /></Field>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Field label="Year"><input type="number" value={form.release_year} onChange={(e) => set("release_year", e.target.value)} className={inp} /></Field>
              <Field label="Runtime (min)"><input type="number" value={form.runtime} onChange={(e) => set("runtime", e.target.value)} className={inp} /></Field>
              <Field label="Rating"><input type="number" step="0.1" value={form.rating} onChange={(e) => set("rating", e.target.value)} className={inp} /></Field>
              <Field label="Maturity"><input value={form.maturity} onChange={(e) => set("maturity", e.target.value)} className={inp} /></Field>
            </div>
            <Field label="Director"><input value={form.director} onChange={(e) => set("director", e.target.value)} className={inp} /></Field>

            <div className="grid grid-cols-3 gap-4 border-t border-white/10 pt-4">
              <Field label="Rent Price ($)"><input data-testid="form-rent-price" type="number" step="0.5" value={form.rent_price} onChange={(e) => set("rent_price", e.target.value)} className={inp} /></Field>
              <Field label="Rent Days"><input type="number" value={form.rent_days} onChange={(e) => set("rent_days", e.target.value)} className={inp} /></Field>
              <Field label="Buy Price ($)"><input data-testid="form-buy-price" type="number" step="0.5" value={form.buy_price} onChange={(e) => set("buy_price", e.target.value)} className={inp} /></Field>
            </div>

            <UploadField label="Poster Image" field="poster_url" value={form.poster_url} accept="image/*" onUploaded={set} />
            <UploadField label="Backdrop Image" field="backdrop_url" value={form.backdrop_url} accept="image/*" onUploaded={set} />
            <UploadField label="Video (mp4 or .m3u8 URL)" field="video_url" value={form.video_url} accept="video/*" onUploaded={set} />
            <UploadField label="Trailer (mp4 or .m3u8 URL)" field="trailer_url" value={form.trailer_url} accept="video/*" onUploaded={set} />

            <div className="grid grid-cols-3 gap-4">
              <Field label="Qualities"><input value={form.qualities} onChange={(e) => set("qualities", e.target.value)} className={inp} /></Field>
              <Field label="Audio Tracks"><input value={form.audio_tracks} onChange={(e) => set("audio_tracks", e.target.value)} className={inp} /></Field>
              <Field label="Subtitles"><input value={form.subtitles} onChange={(e) => set("subtitles", e.target.value)} className={inp} /></Field>
            </div>

            <div className="flex flex-wrap gap-6 py-2">
              {["featured", "trending", "new_release"].map((k) => (
                <label key={k} className="flex items-center gap-2 text-sm capitalize">
                  <Switch checked={form[k]} onCheckedChange={(v) => set(k, v)} /> {k.replace("_", " ")}
                </label>
              ))}
            </div>

            <div className="border-t border-white/10 pt-4">
              <p className="font-display font-bold text-gold mb-3 flex items-center gap-2"><Search className="w-4 h-4" /> SEO</p>
              <div className="space-y-4">
                <Field label="SEO Title"><input data-testid="form-seo-title" value={form.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} className={inp} placeholder="Defaults to title" /></Field>
                <Field label="SEO Meta Description"><textarea rows={2} value={form.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} className={inp} placeholder="Defaults to description" /></Field>
                <Field label="SEO Keywords"><input value={form.seo_keywords || ""} onChange={(e) => set("seo_keywords", e.target.value)} className={inp} placeholder="comma, separated, keywords" /></Field>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/70"><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button data-testid="form-save" onClick={save} disabled={saving} className="bg-brand hover:bg-brand/90 text-white">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} {editingId ? "Save Changes" : "Create Title"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetUser} onOpenChange={(o) => { if (!o) setResetUser(null); }}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><KeyRound className="w-5 h-5 text-gold" /> Reset Password</DialogTitle>
          </DialogHeader>
          <p className="text-white/60 text-sm">Set a new password for <b className="text-white">{resetUser?.email}</b>. The user can sign in with it immediately.</p>
          <input
            data-testid="reset-new-password"
            type="text"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            placeholder="New password (min 6 chars)"
            className={inp}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetUser(null)} className="text-white/70">Cancel</Button>
            <Button data-testid="reset-submit-admin" onClick={submitReset} disabled={resetting} className="bg-brand hover:bg-brand/90 text-white">
              {resetting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={adOpen} onOpenChange={setAdOpen}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><Megaphone className="w-5 h-5 text-gold" /> {adEditId ? "Edit Ad" : "New Ad"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Ad Name"><input data-testid="ad-name" value={adForm.name} onChange={(e) => setAd("name", e.target.value)} className={inp} /></Field>
            <Field label="Placement">
              <select data-testid="ad-placement" value={adForm.placement} onChange={(e) => setAd("placement", e.target.value)} className={inp}>
                <option value="home_banner">Home Page Banner</option>
                <option value="preroll">Video Pre-roll (before playback)</option>
                <option value="content_banner">Content / Detail Banner</option>
              </select>
            </Field>
            <UploadField label="Banner Image" field="image_url" value={adForm.image_url} accept="image/*" onUploaded={setAd} />
            <Field label="Pre-roll Video URL (optional, for pre-roll)"><input value={adForm.video_url} onChange={(e) => setAd("video_url", e.target.value)} className={inp} placeholder="mp4 URL" /></Field>
            <Field label="Click-through Link"><input data-testid="ad-link" value={adForm.link_url} onChange={(e) => setAd("link_url", e.target.value)} className={inp} placeholder="/contact or https://..." /></Field>
            <Field label="Target Content ID (optional — leave blank for global)"><input value={adForm.content_id || ""} onChange={(e) => setAd("content_id", e.target.value)} className={inp} placeholder="e.g. the-golden-pagoda" /></Field>
            <label className="flex items-center gap-2 text-sm"><Switch checked={adForm.active} onCheckedChange={(v) => setAd("active", v)} /> Active</label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdOpen(false)} className="text-white/70">Cancel</Button>
            <Button data-testid="ad-save" onClick={saveAd} className="bg-brand hover:bg-brand/90 text-white">{adEditId ? "Save" : "Create Ad"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdsView({ ads, onNew, onEdit, onRemove }) {
  if (!ads) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-brand animate-spin" /></div>;
  const labels = { home_banner: "Home Banner", preroll: "Pre-roll", content_banner: "Content Banner" };
  return (
    <div>
      <div className="flex justify-end mb-4"><Button data-testid="ad-add-btn" onClick={onNew} className="bg-brand hover:bg-brand/90 text-white"><Plus className="w-4 h-4 mr-1" /> New Ad</Button></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ads.map((a) => (
          <div key={a.id} data-testid={`ad-row-${a.id}`} className="bg-surface border border-white/10 rounded-xl overflow-hidden">
            {a.image_url ? <img src={a.image_url} alt="" className="w-full aspect-video object-cover" /> : <div className="w-full aspect-video bg-ink flex items-center justify-center text-white/20"><Megaphone className="w-8 h-8" /></div>}
            <div className="p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold truncate">{a.name}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${a.active ? "bg-green-500/20 text-green-400" : "bg-white/10 text-white/40"}`}>{a.active ? "Active" : "Off"}</span>
              </div>
              <p className="text-white/40 text-xs mt-1">{labels[a.placement] || a.placement}{a.content_id ? ` · ${a.content_id}` : " · Global"}</p>
              <div className="flex gap-2 mt-3">
                <button data-testid={`ad-edit-${a.id}`} onClick={() => onEdit(a)} className="text-sm bg-white/10 hover:bg-white/20 rounded px-3 py-1.5 flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                <button data-testid={`ad-del-${a.id}`} onClick={() => onRemove(a)} className="text-sm bg-brand/15 text-brand hover:bg-brand/25 rounded px-3 py-1.5 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
          </div>
        ))}
        {ads.length === 0 && <p className="text-white/40 col-span-full text-center py-10">No ads yet. Create one to display on the home page or before videos.</p>}
      </div>
    </div>
  );
}

function SupportView({ support }) {
  if (!support) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-brand animate-spin" /></div>;
  return (
    <div className="space-y-3">
      {support.map((m, i) => (
        <div key={`${m.created_at}-${m.email}-${i}`} data-testid={`support-msg-${i}`} className="bg-surface border border-white/10 rounded-xl p-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <p className="font-semibold flex items-center gap-2"><Inbox className="w-4 h-4 text-gold" /> {m.subject} <span className="text-white/40 font-normal">· {m.name}</span></p>
            <span className="text-white/40 text-xs">{new Date(m.created_at).toLocaleString()}</span>
          </div>
          <p className="text-white/50 text-sm mb-2">{m.email} → {m.to}</p>
          <p className="text-white/80 text-sm whitespace-pre-wrap">{m.message}</p>
        </div>
      ))}
      {support.length === 0 && <p className="text-white/40 text-center py-10">No support messages yet.</p>}
    </div>
  );
}

function SubscribersView({ subs, StatCard, onReset, onSendReset }) {
  if (!subs) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-brand animate-spin" /></div>;
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={<DollarSign className="w-5 h-5" />} label="Gross Revenue" value={`$${subs.total_income}`} />
        <StatCard icon={<DollarSign className="w-5 h-5" />} label="Platform Commission" value={`$${subs.platform_commission}`} />
        <StatCard icon={<ShoppingBag className="w-5 h-5" />} label="Purchases" value={subs.purchases} />
        <StatCard icon={<Users className="w-5 h-5" />} label="Total Users" value={subs.total_users} />
      </div>

      <div className="bg-surface border border-white/10 rounded-xl overflow-x-auto">
        <div className="min-w-[760px]">
          <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-widest text-white/40 border-b border-white/5">
            <div className="col-span-4">User</div>
            <div className="col-span-2">Password</div>
            <div className="col-span-2">Joined</div>
            <div className="col-span-2 text-right">Total Spent</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {subs.subscribers.map((u) => (
            <div key={u.id} data-testid={`sub-row-${u.id}`} className="grid grid-cols-12 items-center px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors text-sm">
              <div className="col-span-4 min-w-0">
                <p className="font-medium truncate flex items-center gap-1">{u.name}{u.role === "admin" && <Crown className="w-3.5 h-3.5 text-gold" />}{u.role === "distributor" && <span className="text-[10px] bg-gold/20 text-gold px-1.5 rounded">SELLER</span>}</p>
                <p className="text-white/40 text-xs truncate">{u.email}</p>
              </div>
              <div className="col-span-2 font-mono text-white/40 tracking-widest">{u.password_masked}</div>
              <div className="col-span-2 text-white/60 text-xs">{fmtDate(u.joined)}</div>
              <div className="col-span-2 text-right font-semibold text-gold">${u.total_paid}</div>
              <div className="col-span-2 flex justify-end gap-1">
                <button data-testid={`send-reset-${u.id}`} onClick={() => onSendReset(u)} title="Email reset link" className="w-8 h-8 rounded-md hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-gold"><Mail className="w-4 h-4" /></button>
                <button data-testid={`reset-pw-${u.id}`} onClick={() => onReset(u)} title="Set new password" className="w-8 h-8 rounded-md hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-gold"><KeyRound className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {subs.subscribers.length === 0 && <p className="text-white/40 text-center py-10">No users yet.</p>}
        </div>
      </div>
    </>
  );
}

const inp = "w-full bg-ink border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-brand";
const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-white/40">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);
